import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Button, Link, List, ListItem } from '@mui/material';
import { Box } from '@mui/system';
import Grid from '@mui/material/Unstable_Grid2';
import { AutocompleteElement, FormContainer, TextFieldElement } from 'react-hook-form-mui';

import useErrorHandling from '@components/common/hooks/useErrorHandling';
import CheckboxButtonGroup from '@components/common/HookFormMui/CheckboxButtonGroup';
import RadioButtonGroup from '@components/common/HookFormMui/RadioButtonGroup';
import RequiredFields from '@components/common/HookFormMui/RequiredFields';
import { participantNeeds, programAreas } from '@lib/constants';
import UserContext from '@lib/contexts/userContext';
import { AbilityContext, Subjects } from '@lib/permissions';
import { toUserOption } from "@lib/user";
import { nullishTernary, sortByLabel, toOption } from '@lib/utils';

const EventForm = ({ form, event, pearsData, staffUsers, availableCurriculum, submitting, onSubmit, valuesSetState }) => {
  const { userState: { userRecord  } } = useContext(UserContext);

  const [additionalOptions, setAdditionalOptions] = useState([]);

  const [audienceWatch, setAudienceWatch] = useState(null);

  const [wasSubmitting, setWasSubmitting] = useState(false);
  const [valuesSet, setValuesSet] = valuesSetState;

  const ability = useContext(AbilityContext);
  const userCanAssign = ability.can('assign', Subjects.EVENT);

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    getValues,
    formState: { errors, isSubmitting },
  } = form;

  const eventTypeWatch = watch('event_type');
  const areaWatch = watch('program_areas');
  const curriculumWatch = watch('curriculum');

  const getUser = (email) => {
    const user = staffUsers.find((u) => u.email === email);
    if (user) {
      return user;
    }
    // User not in myINEP. Could be a former employee or other deleted user.
    return {
      email
    };
  }

  const updateAdditionalOptions = useCallback((selectedCurriculum, selectedAdditional) => {
    const curriculum = selectedCurriculum ? availableCurriculum.find((c) => c.id === selectedCurriculum.id) : null;
    if (curriculum && curriculum.additionals.length) {
      const additionalOpts = curriculum.additionals.map((add) => ({
        id: add.id,
        label: add.name,
      }));
      additionalOpts.unshift({ id: '', label: 'None' });
      setAdditionalOptions(additionalOpts);

      if (selectedAdditional && !curriculum.additionals.find((add) => add.id === selectedAdditional.id)) {
        if (additionalOpts.length) {
          // There are additional options, but the user has selected 'None'
          setValue('additional', { id: '', label: 'None' })
        } else {
          setValue('additional', null);
        }
      }
    } else {
      setAdditionalOptions([]);
      setValue('additional', null);
    }
  }, [availableCurriculum, setValue]);

  /** ERROR HANDLING **/
  useErrorHandling({ errors, isSubmitting, wasSubmitting, setWasSubmitting });

  useEffect(() => {
    const curriculum = availableCurriculum.find(({ id }) => id === curriculumWatch?.id);
    const audience = curriculum?.audience;
    setAudienceWatch(audience);
    if (audience !== 'Adults') {
      setValue('teen_parents', null);
    }
  }, [curriculumWatch]);

  const curriculumOptions = useMemo(() => {
    const areas = areaWatch || getValues('program_areas') || [];

    // Apply filter to curricula based on program area(s)
    const areaFilter = (cur) => {
      return areas.reduce((acc, area) => acc || cur.program_areas?.includes(area), false);
    };
    // Curate list of curriculum names for select
    return availableCurriculum
      .filter(areaFilter)
      .map((item) => ({
        id: item.id,
        label: item.name,
      }))
      .sort(sortByLabel);
  }, [areaWatch, availableCurriculum, valuesSet]);

  useEffect(() => {
    if (!valuesSet) {
      return;
    }
    // Clear selected curriculum (and additional) if it's not in the list
    if (!curriculumWatch || !curriculumOptions.find((c) => c.id === curriculumWatch.id)) {
      setValue('curriculum', null);
      setValue('additional', null);
      setAdditionalOptions([]);
    }
  }, [curriculumOptions, curriculumWatch, setValue, valuesSet]);

  useEffect(() => {
    if (!(areaWatch || []).some((area) => ['snap_ed', 'cphp'].includes(area))) {
      setValue('special_project', null);
    }
  }, [areaWatch, setValue]);

  useEffect(() => {
    if (event === false) {
      // No event means we are creating one
      if (!valuesSet) {
        setValue('created_by', toUserOption(getUser(userRecord?.email)));
        setValuesSet(true);
      }
      return;
    } else if (!event) {
      // Event was not found
      return;
    }
    const values = {
      points: event.points,
      created_by: toUserOption(getUser(event.created_by)),
      event_name: event.event_name,
      event_partner: event.event_partner,
      main_office: event.main_office,
      special_project: event.special_project,
      is_test: event.is_test ? 'true' : 'false',
      event_type: event.event_type,
      additional: event.additional_id
        ? toOption(event.additional_id, event.additional.name)
        : toOption('', 'None'),
      curriculum: event.curriculum_id ? toOption(event.curriculum_id, event.curriculum?.name) : null,
      participant_needs: event.participant_needs,
      participant_needs_other: event.participant_needs_other,
      program_areas: event.program_areas,
      teen_parents: nullishTernary(event.teen_parents, 'true', 'false'),
    };
    Object.keys(values).forEach((key) => setValue(key, values[key]));
    updateAdditionalOptions(values.curriculum, values.additional);
    setValuesSet(true);
  }, [event, setValue, setValuesSet, staffUsers, updateAdditionalOptions, userRecord?.email, valuesSet]);

  const handleEventTypeChange = async (value) => {
    if (!valuesSet) {
      return;
    }
    if (value === 'BOOTH') {
      setValue('curriculum', '');
      setValue('additional', '');
    }
  };

  const updateEventName = () => {
    if (!valuesSet) {
      return;
    }
    const eventName = getValues('event_name');
    if (eventName) {
      return null;
    }

    let curriculum = getValues('curriculum');
    curriculum = curriculum?.label || curriculum;
    let partner = getValues('event_partner');
    partner = partner?.label || partner;
    const args = [];
    if (curriculum) {
      args.push(curriculum.trim());
    }
    if (eventTypeWatch === 'BOOTH') {
      args.push('Booth');
    }
    if (partner) {
      args.push(partner.trim());
    }
    if (args.length === 2) {
      setValue('event_name', args.join(' - '));
    }
  };

  // Update event name when event type changes (booths only)
  // Note: This is not part of handleEventTypeChange because that runs before the value changes.
  useEffect(() => {
    if (eventTypeWatch === 'BOOTH') {
      updateEventName();
    }
  }, [eventTypeWatch, getValues, setValue, updateEventName, valuesSet]);

  const handleCurriculumChange = (_event, value) => {
    const selectedAdditional = getValues('additional');
    updateEventName();
    updateAdditionalOptions(value, selectedAdditional);
  };

  const handlePartnerChange = () => {
    if (!valuesSet) {
      return;
    }
    updateEventName();
  };

  const getFormField = (field) => (
    <ListItem sx={{ display: "list-item" }} key={`field-${field.props.id}`}>
      { field }
    </ListItem>
  );

  const fields = [];

  fields.push(
    <RadioButtonGroup
      id="field-is_test"
      name="is_test"
      label={'What is this event being created for?'}
      placeholder={'Select event purpose'}
      control={control}
      fullWidth
      required
      options={[
        { label: 'Community Event', id: 'false' },
        { label: 'Test Event', id: 'true' },
      ]}
    />
  );

  fields.push(
    <CheckboxButtonGroup
      label="Which program area(s) is delivering this event?"
      id="field-program_areas"
      name="program_areas"
      control={control}
      required
      fullWidth
      options={programAreas}
    />
  );

  fields.push(
    <RadioButtonGroup
      id="field-event_type"
      name="event_type"
      label={'What type of event is this?'}
      placeholder={'Select event type'}
      control={control}
      onChange={handleEventTypeChange}
      fullWidth
      required
      options={[
        { label: 'Direct Education Class(es)', id: 'CLASS' },
        { label: 'Booth', id: 'BOOTH' },
      ]}
    />
  );

  if (eventTypeWatch === 'CLASS') {
    fields.push(
      <AutocompleteElement
        id="field-curriculum"
        name="curriculum"
        label={'What is the primary curriculum for this event?'}
        placeholder={'Choose curriculum'}
        control={control}
        fullWidth
        required
        options={curriculumOptions || []}
        autocompleteProps={{
          onChange: handleCurriculumChange,
          isOptionEqualToValue: (option, newValue) => {
            return option?.id === newValue?.id;
          }
      }}
      />
    );
    if (additionalOptions && additionalOptions.length > 0) {
      fields.push(
        <AutocompleteElement
          id="field-additional"
          name="additional"
          label={'What is the additional curriculum for this event?'}
          placeholder={'Choose additional curriculum'}
          control={control}
          fullWidth
          required
          options={additionalOptions}
          autocompleteProps={{
            isOptionEqualToValue: (option, newValue) => {
              return option?.id === newValue?.id;
            }
          }}
        />
      );
    }
  }

  if (audienceWatch === 'Adults') {
    fields.push(
      <RadioButtonGroup
        id="field-teen_parents"
        name="teen_parents"
        label={'Will this event include teen parents under the age of 18?'}
        control={control}
        fullWidth
        required
        options={[
          { label: 'Yes', id: 'true' },
          { label: 'No', id: 'false' },
        ]}
      />
    );
  }

  /**
   * PEARS field
   */
  fields.push(
    <AutocompleteElement
      id="field-event_partner"
      name="event_partner"
      label={'Which partner are you coordinating this event with?'}
      textFieldProps={{
        helperText: <>If you cannot find a site, please email <Link href="mailto:uie-pears-notifications@illinois.edu">uie-pears-notifications@illinois.edu</Link> to request to add the site.</>
      }}
      control={control}
      fullWidth
      required
      options={pearsData?.partners || []}
      autocompleteProps={{
        onChange: handlePartnerChange,
        isOptionEqualToValue: (option, newValue) => {
          return option === newValue;
        }
      }}
    />
  );

  fields.push(
    <CheckboxButtonGroup
      label="What are the specific needs of this group that you will consider while planning this event? Select all that apply."
      id="field-participant_needs"
      name="participant_needs"
      control={control}
      maxColumns={2}
      required
      fullWidth
      textFieldRequired
      options={participantNeeds}
    />
  );

  /**
   * PEARS field
   */
  fields.push(
    <AutocompleteElement
      id="field-main_office"
      name="main_office"
      label={'What is your main office?'}
      placeholder={'Choose main office'}
      control={control}
      fullWidth
      required
      options={pearsData?.main_offices || []}
      autocompleteProps={{
        isOptionEqualToValue: (option, newValue) => {
          return option === newValue;
        }
      }}
    />
  );

  /**
   * myINEP field
   */
  fields.push(
    <AutocompleteElement
      id="field-created_by"
      name="created_by"
      label={'Created By'}
      helperText={'Name and email address of the user assigned to this event.'}
      placeholder={'Choose assignee'}
      control={control}
      fullWidth
      required
      options={(staffUsers ? staffUsers.map(toUserOption) : [])}
      autocompleteProps={{
        readOnly: !userCanAssign,
        isOptionEqualToValue: (option, newValue) => {
          return option?.id === newValue?.id;
        }
      }}
    />
  );

  /**
   * Autogenerated at first
   */
  fields.push(
    <TextFieldElement
      id="field-event_name"
      name="event_name"
      label={'Event Name'}
      control={control}
      required
      fullWidth
    />
  );

  /**
   * PEARS field
   */
  if ((areaWatch || []).some((area) => ['snap_ed', 'cphp'].includes(area))) {
    fields.push(
      <AutocompleteElement
        id="field-special_project"
        name="special_project"
        label={'Which SNAP-Ed Special Project is this event part of?'}
        placeholder={''}
        control={control}
        fullWidth
        required
        options={pearsData?.special_projects || []}
        autocompleteProps={{
          isOptionEqualToValue: (option, newValue) => {
            return option === newValue;
          }
        }}
      />
    );
  }

  return (
    <Box width={'100%'}>
      { !!valuesSet && (
        <FormContainer onSuccess={handleSubmit(onSubmit)} FormProps={{ className: 'mui' }}>
          <RequiredFields />
          <Grid container columns={12} spacing={2}>
            <Grid xs={12} sm={10} md={8}>
              <List sx={{
                  listStyle: 'decimal',
                  paddingLeft: 2,
                }}>
                { fields.map(getFormField) }
              </List>
            </Grid>

            <Grid xs={12} sm={10} md={8}>
              <Button
                id="button-submit"
                disabled={submitting}
                type={'submit'}
                variant="contained"
                size="small"
                sx={{ marginTop: '20px' }}
              >
                Save
              </Button>
            </Grid>
          </Grid>
        </FormContainer>
      )}
    </Box>
  );
};

export default EventForm;
